import React, { useEffect } from "react";
import { Attachment } from "src/types/Attachment";
import { SubmissionCommunicationEntity } from "src/types/enums/SubmissionCommunicationEntity";
import { faCalendarAlt } from "@fortawesome/free-solid-svg-icons";
import { clickUrlDownload } from "src/hooks/useFileDownload";
import { useGetCommunicationAttachmentsQuery } from "src/services";
import Loader from "src/components/Loader";
import { displayDownloadStarted, toastError } from "src/utils/toast";
import styled from "styled-components";
import { useCreateCNCSubmissionDetailsExportMutation } from "src/services/export-service-api";
import { Button, Icon } from "semantic-ui-react";
import { LIGHT_GRAY } from "@120wateraudit/waterworks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CNCSubmission } from "src/types/Submission";
import { formatDate } from "src/utils/format";

interface Props {
  closeModal: () => void;
  cncSubmission: CNCSubmission;
  accountId: number;
  pwsName: string;
  pwsId: string;
}

const methodsOfDistributionNotices = [
  { value: "Mailed with water bill", key: "mailedWithWaterBill" },
  { value: "Mailed with CCR", key: "mailedWithCCR" },
  { value: "Separate mail", key: "separateMail" },
  { value: "Hand delivery", key: "handDelivery" },
  { value: "Email", key: "email" },
  { value: "Other", key: "otherDeliveryMethod" }
];

const ViewConsumerNoticeModalV2: React.FC<Props> = ({
  cncSubmission,
  accountId,
  pwsName,
}) => {
  const { data, id, submittedOn, system: { pwsId } } = cncSubmission;

  const {
    submitterName,
    submitterEmail,
    submitterPhone,
    leadNoticeDistributions,
    leadNoticeDistributionsDate,
    grrNoticeDistributions,
    grrNoticeDistributionsDate,
    unknownNoticeDistributions,
    unknownNoticeDistributionsDate,
    mailedWithWaterBill,
    mailedWithCCR,
    separateMail,
    handDelivery,
    email,
    otherDeliveryMethod,
  } = data;

  const [createCNCSubmissionDetailsExport, { isSuccess, isError }] =
    useCreateCNCSubmissionDetailsExportMutation();

  const { data: useGetCommunicationsLetters, isLoading: isLoadingLetters } =
    useGetCommunicationAttachmentsQuery({
      entity: SubmissionCommunicationEntity.submissionsCommunicationsLetters,
      submissionId: id,
      accountId,
      useCNCSubmissions: true,
    });
  const {
      data: useGetCommunicationsCertificate,
      isLoading: isLoadingCommunicationsCertificate,
    } = useGetCommunicationAttachmentsQuery({
      entity: SubmissionCommunicationEntity.submissionsCommunicationsCertificates,
      submissionId: id,
      accountId,
      useCNCSubmissions: true,
    });

  useEffect(() => {
    if (isSuccess) {
      displayDownloadStarted({ type: "export" });
    }
    if (isError) {
      toastError("Error exporting customer notice responses");
    }
  }, [isSuccess, isError]);
  return (
    <ChangeStatusContainer>
      <HeaderContainer>
        <h3>Customer Notice Responses</h3>
        <DownloadResponseButton
          onClick={async () => {
            await createCNCSubmissionDetailsExport(id);
          }}
        />
      </HeaderContainer>
      <div style={{ display: "flex" }}>
        <div style={{ width: "40%" }}>
          <DetailField title="PWS ID" value={pwsId?.toString()} />
        </div>
        <div style={{ width: "40%" }}>
          <DetailField title="PWS Name" value={pwsName} />
        </div>
        <div style={{ width: "20%" }}>
          <DetailField
            title="Submitted Date"
            value={
              submittedOn
                ? new Date(submittedOn).toLocaleDateString(undefined, {
                    month: "2-digit",
                    day: "2-digit",
                    year: "numeric",
                  })
                : ""
            }
          />
        </div>
      </div>
      <InlineWrapper>
        <div style={{ width: "40%" }}>
          <DetailField title="Submitter Name" value={submitterName} />
        </div>
        <div style={{ width: "40%" }}>
          <DetailField title="Submitter Email" value={submitterEmail} />
        </div>
        <div style={{ width: "20%" }}>
          <DetailField title="Submitter Phone" value={submitterPhone} />
        </div>
      </InlineWrapper>
      <Header>
        <span>
          During this submission period, by what method did you distribute
          notices?
        </span>
        <br />
        <span>Select all that apply.</span>
      </Header>
      <MethodsOfDistributionNoticesContainer>
        {methodsOfDistributionNotices.map(({ value, key }) => (
          <FieldSelectBubble key={key}>
            <Bubble
              selected={
                {
                  mailedWithWaterBill,
                  mailedWithCCR,
                  separateMail,
                  handDelivery,
                  email,
                  otherDeliveryMethod
                }[key]
              }
            />
            <span>{value}</span>
          </FieldSelectBubble>
        ))}
      </MethodsOfDistributionNoticesContainer>
      <DetailField
        title="During this submission period, how many 'Lead' notices have you distributed"
        value={leadNoticeDistributions?.toString() ?? 'N/A'}
      />
      <DetailField
        title="When did you complete sending your notices?"
        value={formatDate(leadNoticeDistributionsDate)}
        date
      />
      <DetailField
        title="During this submission period, how many 'GRR' notices have you distributed?"
        value={grrNoticeDistributions?.toString() ?? 'N/A'}
      />
      <DetailField
        title="When did you complete sending your notices?"
        value={formatDate(grrNoticeDistributionsDate)}
        date
      />
      <DetailField
        title="During this submission period, how many 'Unknown' notices have you distributed?"
        value={unknownNoticeDistributions?.toString() ?? 'N/A'}
      />
      <DetailField
        title="When did you complete sending your notices?"
        value={formatDate(unknownNoticeDistributionsDate)}
        date
      />
      <DownloadAttachments
        name="Communications Certificate"
        attachments={useGetCommunicationsCertificate}
        isLoading={isLoadingCommunicationsCertificate}
        hideEmpty
      />
      <DownloadAttachments
        name="Letters"
        attachments={useGetCommunicationsLetters}
        isLoading={isLoadingLetters}
      />
    </ChangeStatusContainer>
  );
};

interface FieldProps {
  title: string;
  value?: string;
  date?: boolean;
}

const DetailField: React.FC<FieldProps> = ({ title, value, date }) => (
  <FieldWrapper date={date}>
    <FieldTitle date={date}>{title}</FieldTitle>
    <FieldValue>
      {date && (
        <FontAwesomeIcon
          size="2x"
          icon={faCalendarAlt}
          style={{ color: LIGHT_GRAY, paddingRight: "5px", width: "18px" }}
        />
      )}
      <FieldValueSpan date={date}>{value}</FieldValueSpan>
    </FieldValue>
  </FieldWrapper>
);

interface DownloadAttachmentsProps {
  name: string;
  attachments: any;
  isLoading: boolean;
  hideEmpty?: boolean;
}

const DownloadAttachments: React.FC<DownloadAttachmentsProps> = ({
  name,
  attachments,
  isLoading,
  hideEmpty = false
}) => {
  if (hideEmpty && isLoading) {
    return null;
  }

  if (hideEmpty && attachments?.data.length === 0) {
    return null;
  }

   return (
     <DownloadContainer>
       <FieldTitle style={{ width: '100%' }}>Download {name}</FieldTitle>
       {isLoading && <Loader />}

       {!isLoading && (
         <AttachmentList>
           {(attachments?.data.length === 0) && (
               <div>No {name} to download</div>
           )}
           {attachments?.data.length > 0 &&
             attachments?.data.map((attachment: Attachment) => (
               <AttachmentItem key={attachment.id}>
                 <AttachmentName>{attachment.originalFileName}</AttachmentName>
                 <DownloadAttachmentButton url={attachment.url} />
               </AttachmentItem>
             ))}
         </AttachmentList>
       )}
     </DownloadContainer>
   );
}

const DownloadResponseButton: React.FC<{ onClick: () => unknown }> = ({
  onClick,
}) => (
  <Button basic onClick={onClick}>
    <Icon name={"download"} />
    <DownloadButtonText>Download Responses</DownloadButtonText>
  </Button>
);

const DownloadAttachmentButton: React.FC<{ url: string }> = ({ url }) => (
  <Button
    basic
    onClick={() => {
      try {
        clickUrlDownload(url);
      } catch (error: unknown) {
        console.error(error);
        toastError("Error downloading file");
      }
    }}
  >
    <Icon name={"download"} />
    <DownloadButtonText>Download</DownloadButtonText>
  </Button>
);

const ChangeStatusContainer = styled.div`
  margin: 0 32px 32px;
`;

const InlineWrapper = styled.div`
  display: flex;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 30px;
  h3 {
    margin: 0;
  }
`;

const Header = styled.div`
  line-height: normal;
  font-weight: bold;
  padding: 15px 0 5px 0;
`;

const FieldWrapper = styled.div<{ date?: boolean }>`
  display: flex;
  flex-direction: column;
  ${({ date }) => date && "padding-bottom: 15px"};
`;

const FieldTitle = styled.div<{ date?: boolean }>`
  font-weight: ${({ date }) => (date ? "300" : "bold")};
`;

const FieldValue = styled.div`
  display: flex;
  align-items: center;
  font-weight: 400;
  padding: 10px 0;
`;

const FieldValueSpan = styled.span<{ date?: boolean }>`
  ${({ date }) => date && "padding-left: 5px"};
`;

const MethodsOfDistributionNoticesContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 15px;
`;

const Bubble = styled.div<{ selected: boolean | undefined }>`
  background-color: ${({ selected }) => (selected ? "black" : "white")};
  border-radius: 50%;
  width: 18px;
  height: 18px;
  border: 1px solid black;
`;

const FieldSelectBubble = styled.div`
  margin: 8px 0;
  display: flex;
  align-self: start;

  span {
    padding-left: 5px;
  }
`;

const DownloadContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 32px;
`;

const AttachmentList = styled.ul`
  list-style: none;
  padding: 0;
  width: 100%;
  margin: 0;
`;

const AttachmentItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #ddd;
`;

const AttachmentName = styled.span`
  flex: 1;
`;

const DownloadButtonText = styled.span`
  font-weight: 600;
`;

export default ViewConsumerNoticeModalV2;
